// Centralised hangup logic. Multiple sites can end a call:
//   - End-video button in Conversation header
//   - Cancel button on a pending outgoing invite (in Conversation header)
//   - Conversation route unmounting while inVideo === true (agent navigated away)
//   - Inbound webrtc:hangup from the other party
//   - Inbound visitor:presence with status='departed' during a live call
//
// All of them funnel through `endCallLocally()` so the cleanup is consistent.
// `hangupAndNotify()` adds the outbound `webrtc:hangup` emission for the
// cases where WE are the source of the hangup; inbound paths skip the emit
// to avoid echo loops.

import type { MessageId, SessionId } from '@prakash-software/shared';
import { getBus } from '@prakash-software/shared';
import { useAgentStore } from '../stores/agent';
import { formatDuration, markEnd } from './call-timer';
import {
  useConversationsStore,
  type ConversationMessage,
} from '../stores/conversations';
import { useOutgoingCallsStore } from '../stores/outgoing-calls';

type Reason = 'ended' | 'cancelled' | 'left';
type Source = 'self' | 'remote' | 'system';

/** Emit an in-transcript system annotation: local-append + bus emit so the
 *  server persists and the visitor receives a mirrored chip. Idempotent via
 *  per-message UUIDs and the dedupe in the bus subscriber on each side.
 *  Exported so other agent surfaces (handoff/takeback in Conversation.tsx)
 *  can produce persistent system chips through the same path. */
export function emitSystemMessage(sessionId: SessionId, text: string, at: number = Date.now()): void {
  const id = crypto.randomUUID() as MessageId;
  const msg: ConversationMessage = {
    id,
    sessionId,
    author: 'system',
    text,
    sentAt: at,
  };
  // Idempotency guard for the local append — duplicate emit (e.g. two
  // hangup paths firing in quick succession) would otherwise double-print.
  const existing = useConversationsStore.getState().byId.get(sessionId);
  if (!existing?.messages.some((m) => m.id === id)) {
    useConversationsStore.getState().append(sessionId, msg);
  }
  try {
    getBus().emit({
      type: 'system:message',
      sessionId,
      messageId: id,
      text,
      at,
    });
  } catch {
    /* bus may not be live; local append already happened */
  }
}

/** Local cleanup only. Used by inbound-hangup handlers (no echo). */
export function endCallLocally(
  sessionId: SessionId,
  opts: { reason: Reason; source: Source; remoteName?: string } = {
    reason: 'ended',
    source: 'self',
  },
): void {
  const convStore = useConversationsStore.getState();
  const conv = convStore.byId.get(sessionId);
  if (conv) {
    if (conv.inVideo) convStore.setVideo(conv.sessionId, false);
    const at = Date.now();
    // Compute call duration; clears the timer on this call.
    const durationMs = markEnd(sessionId, at);
    const durationSuffix =
      durationMs !== null && durationMs >= 1000
        ? ` · ${formatDuration(durationMs)}`
        : '';
    // Ambient text — both sides see the same chip. Only the initiator
    // emits + persists; the other side receives via the system:message
    // subscriber. Skipping the remote-source branch avoids double-chips.
    if (opts.source !== 'remote') {
      const baseText = opts.reason === 'cancelled' ? 'Call cancelled.' : 'Call ended.';
      const text = `${baseText}${durationSuffix}`;
      emitSystemMessage(conv.sessionId, text, at);
    }
  }
  // Always clear caller-mode and any pending outgoing invite for this session.
  useOutgoingCallsStore.getState().drop(sessionId);
  useOutgoingCallsStore.getState().clearCaller(sessionId);
}

/**
 * Claim ownership of the conversation. Used whenever an agent engages with
 * a previously AI-owned (or ownership-less) session — accepting an incoming
 * call, or the other party accepting our outgoing invite. Emits `ai:escalate`
 * so the visitor's widget flips its banner and the server orchestrator stops
 * replying. Idempotent — re-claiming a session you already own is a no-op.
 */
export function claimOwnership(
  sessionId: SessionId,
  reason: 'accepted-incoming-call' | 'visitor-accepted-call' | 'manual',
): void {
  const me = useAgentStore.getState().current;
  if (!me) return;
  const convStore = useConversationsStore.getState();
  const conv = convStore.byId.get(sessionId);
  if (!conv) return;
  const alreadyMine =
    conv.ownedBy === 'agent' && conv.ownerAgentId === me.agentId;
  if (alreadyMine) return;

  convStore.setOwner(sessionId, 'agent', me.agentId);

  // Tell the visitor's widget the human took over. Re-uses ai:escalate with
  // trigger='agent-now-available' — same path as the manual Take-over button.
  try {
    getBus().emit({
      type: 'ai:escalate',
      sessionId,
      visitorId: conv.visitorId,
      trigger: 'agent-now-available',
      at: Date.now(),
    });
  } catch {
    /* fire-and-forget */
  }

  // Persistent system chip — survives reload, mirrored to the visitor.
  const at = Date.now();
  const text =
    reason === 'accepted-incoming-call'
      ? `${me.name} joined the conversation.`
      : reason === 'visitor-accepted-call'
        ? `${me.name} joined the call.`
        : `${me.name} took over from the AI.`;
  emitSystemMessage(sessionId, text, at);
}

/** Local cleanup + emit webrtc:hangup to the other side. */
export function hangupAndNotify(
  sessionId: SessionId,
  reason: Reason = 'ended',
): void {
  const me = useAgentStore.getState().current;
  if (me) {
    try {
      getBus().emit({
        type: 'webrtc:hangup',
        sessionId,
        from: me.agentId,
        reason,
        at: Date.now(),
      });
    } catch {
      /* fire-and-forget */
    }
  }
  endCallLocally(sessionId, { reason, source: 'self' });
}
